<script setup lang="ts">
import { Dialog, Button, InputText, InputNumber, MultiSelect } from 'primevue';
import { computed, ref } from 'vue';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import { storeToRefs } from 'pinia';
import { useCreatePackageType } from '../api/createPackageType';
import { useUpdatePackageType } from '../api/updatePackageType';
import { usePackageTypeStore } from 'shared/packages/stores/packageTypeStore';
import { usePackagingStore } from 'shared/packages/stores/packagingStore';
import MeasurementInput from '@/common/components/MeasurementInput.vue';

const emit = defineEmits<{
  saved: [id: string];
}>();

defineExpose({
  show,
  showCopy,
});

const languageStore = useLanguageStore();
const { translations } = storeToRefs(languageStore);
const pageTranslations = computed(() => translations.value.packageTypes);

const packageTypeStore = usePackageTypeStore();
const packagingStore = usePackagingStore();
const createPackageType = useCreatePackageType();
const updatePackageType = useUpdatePackageType();

const visible = ref(false);
const loading = ref(false);
const packageTypeId = ref<string | null>(null);
const name = ref('');
const pieces = ref<number>(0);
const layers = ref<number | null>(null);
const piecesPerLayer = ref<number | null>(null);
const spacerHeight = ref<number | null>(null);
const packagingIds = ref<string[]>([]);
const fieldTranslations = computed(() => translations.value.packages.fields);

// Pieces follows layers x pieces per layer but stays editable.
function setLayering(field: 'layers' | 'piecesPerLayer', value: number | null) {
  (field === 'layers' ? layers : piecesPerLayer).value = value;
  if (layers.value !== null && piecesPerLayer.value !== null) {
    pieces.value = layers.value * piecesPerLayer.value;
  }
}

const packagingOptions = computed(() => {
  return packagingStore.sortedByName.map((p) => ({ label: p.name, value: p.id }));
});

const title = computed(() => {
  return packageTypeId.value ? pageTranslations.value.editPackageType : pageTranslations.value.addNewPackageType;
});

function show(id?: string) {
  if (!id) {
    packageTypeId.value = null;
    name.value = '';
    pieces.value = 0;
    layers.value = null;
    piecesPerLayer.value = null;
    spacerHeight.value = null;
    packagingIds.value = [];
    visible.value = true;
    return;
  }

  const item = packageTypeStore.getById(id);
  if (!item) {
    return;
  }

  packageTypeId.value = item.id;
  name.value = item.name;
  pieces.value = item.pieces;
  layers.value = item.layers;
  piecesPerLayer.value = item.piecesPerLayer;
  spacerHeight.value = item.spacerHeight;
  packagingIds.value = [...item.packagingIds];
  visible.value = true;
}

function showCopy(id: string) {
  const item = packageTypeStore.getById(id);
  if (!item) {
    return;
  }

  packageTypeId.value = null;
  name.value = '';
  pieces.value = item.pieces;
  layers.value = item.layers;
  piecesPerLayer.value = item.piecesPerLayer;
  spacerHeight.value = item.spacerHeight;
  packagingIds.value = [...item.packagingIds];
  visible.value = true;
}

async function save() {
  if (!name.value.trim()) return;

  loading.value = true;
  try {
    const payload = {
      name: name.value,
      pieces: pieces.value,
      layers: layers.value,
      piecesPerLayer: piecesPerLayer.value,
      spacerHeight: spacerHeight.value,
      packagingIds: [...packagingIds.value],
    };

    const response = packageTypeId.value
      ? await updatePackageType.execute({ id: packageTypeId.value, entity: payload })
      : await createPackageType.execute({ entity: payload });

    if (!response.ok) {
      return;
    }

    visible.value = false;
    emit('saved', response.packageType.id);
  } finally {
    loading.value = false;
  }
}
</script>

<template>
  <Dialog v-model:visible="visible" modal :header="title" :style="{ width: '38rem' }">
    <div class="form-grid">
      <label>{{ translations.generic.name }}</label>
      <InputText v-model="name"></InputText>

      <label>{{ fieldTranslations.layers }}</label>
      <InputNumber :modelValue="layers" :min="0" showButtons @update:modelValue="setLayering('layers', $event)"></InputNumber>

      <label>{{ fieldTranslations.piecesPerLayer }}</label>
      <InputNumber :modelValue="piecesPerLayer" :min="0" showButtons @update:modelValue="setLayering('piecesPerLayer', $event)"></InputNumber>

      <label>{{ translations.generic.amount }}</label>
      <InputNumber v-model="pieces" :min="0" showButtons></InputNumber>

      <label>{{ fieldTranslations.spacerHeight }}</label>
      <MeasurementInput v-model="spacerHeight" unitKey="package-type-spacer-height" :min="0"></MeasurementInput>

      <label>{{ translations.sideMenu.packaging }}</label>
      <MultiSelect
        v-model="packagingIds"
        :options="packagingOptions"
        optionLabel="label"
        optionValue="value"
        display="chip"
      ></MultiSelect>
    </div>

    <template #footer>
      <Button :label="translations.generic.cancel" text @click="visible = false"></Button>
      <Button :label="translations.generic.save" :loading="loading" @click="save"></Button>
    </template>
  </Dialog>
</template>

<style scoped>
.form-grid {
  display: grid;
  grid-template-columns: 10rem 1fr;
  gap: 0.75rem;
  align-items: center;
}
</style>
